import {
  mergeAttributes,
  Node,
  NodeViewWrapper,
  ReactNodeViewRenderer
} from '@tiptap/react';
import { type InputHTMLAttributes, useEffect, useRef } from 'react';

export interface Attributes {
  id: string;
  isReadOnly: boolean;
  placeholder: string;
  value: string;
}

const NumberFieldView: Parameters<typeof ReactNodeViewRenderer>[0] = ({
  editor,
  getPos,
  node,
  selected,
  updateAttributes
}) => {
  const { isReadOnly, placeholder, value } = node.attrs as Attributes;

  const ref = useRef<HTMLInputElement>(null);

  const handleChange: InputHTMLAttributes<HTMLInputElement>['onChange'] =
    event => {
      if (Number.isNaN(+event.target.value)) return;

      updateAttributes({ value: event.target.value });
    };

  const handleKeyDown: InputHTMLAttributes<HTMLInputElement>['onKeyDown'] =
    event => {
      const pos = getPos();

      if (ref.current && pos) {
        const cursorPosition = ref.current.selectionStart;

        if (cursorPosition === 0 && event.key === 'ArrowLeft') {
          editor.chain().focus().setTextSelection(pos).run();
        }

        if (cursorPosition === value.length && event.key === 'ArrowRight') {
          editor
            .chain()
            .focus()
            .setTextSelection(pos + 1)
            .run();
        }
      }
    };

  const { from, to } = editor.state.selection;

  useEffect(() => {
    if (!selected || !ref.current || to - from > 1) return;

    ref.current.focus();
  }, [from, selected, to]);

  return (
    <NodeViewWrapper
      as="input"
      className="placeholder:text-muted-foreground dark:bg-input/30 border-input focus-visible:border-ring focus-visible:ring-ring/50 inline-block field-sizing-content h-8 max-w-full rounded-sm border bg-transparent px-2 shadow-xs transition-[color,box-shadow] outline-none placeholder:text-sm focus-visible:ring-[3px] disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50"
      data-slot="input"
      inputMode="numeric"
      onChange={handleChange}
      onKeyDown={handleKeyDown}
      pattern="[0-9]*"
      placeholder={placeholder}
      readOnly={isReadOnly}
      ref={ref}
      tabIndex={-1}
      value={value}
    />
  );
};

const NumberField = Node.create({
  addAttributes() {
    return {
      id: {
        default: '',
        parseHTML: element => element.getAttribute('data-id'),
        renderHTML: (attrs: Attributes) => ({ 'data-id': attrs.id })
      },
      isReadOnly: {
        default: false,
        parseHTML: element => element.getAttribute('data-is-read-only'),
        renderHTML: (attrs: Attributes) => ({
          'data-is-read-only': attrs.isReadOnly
        })
      },
      placeholder: {
        default: null,
        parseHTML: element => element.getAttribute('data-placeholder'),
        renderHTML: (attrs: Attributes) => ({
          'data-placeholder': attrs.placeholder
        })
      },
      value: {
        default: '',
        parseHTML: element => element.getAttribute('data-value'),
        renderHTML: (attrs: Attributes) => ({
          'data-value': attrs.value
        })
      }
    };
  },
  addNodeView() {
    return ReactNodeViewRenderer(NumberFieldView, {
      className: 'inline-block max-w-full'
    });
  },
  atom: true,
  group: 'inline',
  inline: true,
  name: 'numberField',
  parseHTML() {
    return [{ tag: 'span[data-number-field]' }];
  },
  renderHTML({ HTMLAttributes }) {
    return [
      'span',
      mergeAttributes(HTMLAttributes, { 'data-number-field': '' }),
      0
    ];
  }
});

export default NumberField;
